import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ButtonBase, Icon, makeStyles } from '@material-ui/core';
import AnesthesiaIcon from '@/assets/images/anesthesia.svg?react';
import PopupSelectMenu from '../schedule/shared/PopupSelectMenu';
import { setPreOpBoardAnesthesiologist } from '@/graph/patients';

const anesthesiologistsQuery = gql`
  query PreOpBoardAnesthesiologists {
    anesthesiologists {
      id
      name
    }
  }
`;

const useStyles = makeStyles({
  trigger: {
    font: 'inherit',
    minHeight: 44,
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});

export default function PreOpBoardMdaPicker({
  patientId,
  value,
  onError,
}: {
  patientId: string | number;
  value?: { id?: string | number; name?: string | null } | null;
  onError: (message: string) => void;
}) {
  const classes = useStyles();
  const { data, error } = useQuery<{ anesthesiologists: { id: number; name: string }[] }>(anesthesiologistsQuery, {
    onError: () => onError('Unable to load anesthesiologists. Please refresh and try again.'),
  });
  const [save] = useMutation(setPreOpBoardAnesthesiologist);
  const select = async (anesthesiologistId: number | null) => {
    try {
      const result = await save({ variables: { patientId: Number(patientId), anesthesiologistId } });
      if (!result.data?.setPreOpBoardAnesthesiologist) throw new Error('Assignment was not saved');
    } catch {
      onError('Unable to update the PreOp Board anesthesiologist. Please try again.');
    }
  };

  return (
    <PopupSelectMenu<number>
      options={data ? data.anesthesiologists.map(a => ({ id: a.id, label: a.name })) : error ? [] : undefined}
      selectedId={value?.id == null ? undefined : Number(value.id)}
      onSelect={select}
      onRemove={() => select(null)}
      removeOption="Clear assignment"
      menuTitle="PreOp Board anesthesiologist"
      renderTrigger={({ onClick }) => (
        <ButtonBase
          className={classes.trigger}
          onClick={onClick}
          aria-label={value?.name ? `Change board anesthesiologist: ${value.name}` : 'Assign board anesthesiologist'}
        >
          {value?.name ? (
            <span className={classes.name} title={value.name}>
              {value.name}
            </span>
          ) : (
            <Icon>
              <AnesthesiaIcon width={22} height={22} />
            </Icon>
          )}
        </ButtonBase>
      )}
    />
  );
}
